/* src/pages/FormBuilderList.jsx
 * Form Builder - list and manage forms
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { db } from '../firebase';
import { getFormLimitForTier, resolveEffectiveTier } from '../utils/tiers';
import { usageApi } from '../utils/api';
import { usagePillText } from '../utils/usagePill';
import TemplateSelector from '../components/TemplateSelector';
import EmbedSettings from '../components/EmbedSettings';
import { useToast } from '../contexts/ToastContext';
import { buildNewFormData } from '../utils/formTemplates';
import { pickWorkspaceDisplayName } from '../utils/replyIdentity';
import { useIsMobile } from '../hooks/useIsMobile';
import { FormsListSkeleton } from '../components/Skeleton';

export default function FormBuilderList() {
  const { currentUser, userProfile, logout } = useAuth();
  const isMobile = useIsMobile();
  const { selectedWorkspaceId, workspaceReady } = useWorkspace();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showEmbedSettings, setShowEmbedSettings] = useState(false);
  const [selectedFormForEmbed, setSelectedFormForEmbed] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewForm, setPreviewForm] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop'); // 'desktop' or 'mobile'
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const unsubscribeRef = useRef(null);

  useEffect(() => {
    console.log('[FORM BUILDER LIST] useEffect triggered:', { 
      hasUser: !!currentUser, 
      workspaceId: selectedWorkspaceId,
      currentWorkspace: selectedWorkspaceId 
    });
    
    if (currentUser && selectedWorkspaceId) {
      console.log('[FORM BUILDER LIST] ✅ Conditions met - setting up real-time listener for workspace:', selectedWorkspaceId);
      setupRealtimeListener();
    } else {
      console.log('[FORM BUILDER LIST] ⏳ Waiting for user/workspace:', { 
        hasUser: !!currentUser, 
        workspaceId: selectedWorkspaceId 
      });
      setLoading(false);
    }

    // Cleanup: unsubscribe when component unmounts or workspace changes
    return () => {
      if (unsubscribeRef.current) {
        console.log('[FORM BUILDER LIST] 🧹 Cleaning up real-time listener');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [currentUser, selectedWorkspaceId]);

  useEffect(() => {
    if (!currentUser || !selectedWorkspaceId) {
      setUsage(null);
      setUsageLoading(false);
      return undefined;
    }
    let cancelled = false;
    setUsageLoading(true);
    usageApi.getUsage(selectedWorkspaceId)
      .then((res) => {
        if (cancelled) return;
        setUsage(res?.success ? res : null);
      })
      .catch(() => {
        if (!cancelled) setUsage(null);
      })
      .finally(() => {
        if (!cancelled) setUsageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser, selectedWorkspaceId]);

  function setupRealtimeListener() {
    if (!currentUser || !selectedWorkspaceId) {
      console.log('[FORM BUILDER LIST] Cannot setup listener - missing user or workspace:', { user: !!currentUser, workspace: selectedWorkspaceId });
      setLoading(false);
      return;
    }

    // Clean up previous listener if exists
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    try {
      setLoading(true);
      console.log('[FORM BUILDER LIST] 🔄 Setting up real-time listener for workspace:', selectedWorkspaceId);
      
      // Set up real-time listener for forms
      const formsRef = collection(db, 'forms');
      const q = query(formsRef, where('workspaceId', '==', selectedWorkspaceId));
      
      console.log('[FORM BUILDER LIST] 📡 Starting Firestore onSnapshot listener...');
      
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          console.log('[FORM BUILDER LIST] 📥 Real-time update received:', snapshot.docs.length, 'forms');
          
          const formsList = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            console.log('[FORM BUILDER LIST] Form:', { id: doc.id, name: data.name, workspaceId: data.workspaceId });
            formsList.push({ id: doc.id, ...data });
          });
          
          console.log('[FORM BUILDER LIST] ✅ Updated forms list:', formsList.length, 'forms');
          setForms(formsList);
          setLoading(false);
        },
        (error) => {
          console.error('[FORM BUILDER LIST] ❌ Real-time listener error:', error);
          setLoading(false);
        }
      );
      
      unsubscribeRef.current = unsubscribe;
      console.log('[FORM BUILDER LIST] ✅ Real-time listener active');
    } catch (error) {
      console.error('[FORM BUILDER LIST] Error setting up real-time listener:', error);
      setLoading(false);
    }
  }

  function handleCreateForm() {
    setShowTemplateSelector(true);
  }

  async function createNewForm({ template, layout, theme: customTheme }) {
    if (isCreating) return;
    if (!currentUser || !workspaceReady || !selectedWorkspaceId) return;

    if (
      selectedWorkspaceId !== currentUser.uid &&
      selectedWorkspaceId
    ) {
      showToast('Creating form in a team workspace', 'info');
    }

    const tier = resolveEffectiveTier(userProfile);
    if (tier === 'starter') {
      const formLimit = getFormLimitForTier(tier);
      if (formLimit > 0 && forms.length >= formLimit) {
        showToast(
          `Your ${tier} plan includes ${formLimit} form. Upgrade on Billing to add more.`,
          'error'
        );
        navigate('/billing');
        return;
      }
    }

    setIsCreating(true);
    try {
      // Stamp a reply "Shown as" name from the workspace so no form is ever
      // created without a brand name (never form.name — see replyIdentity.js).
      let businessName = '';
      try {
        const workspaceDoc = await getDoc(doc(db, 'workspaces', selectedWorkspaceId));
        if (workspaceDoc.exists()) {
          const wsData = workspaceDoc.data();
          businessName = pickWorkspaceDisplayName(wsData.businessName, wsData.name);
        }
      } catch (wsError) {
        console.error('Error loading workspace for businessName default:', wsError);
      }

      const formData = buildNewFormData({
        template,
        layout,
        theme: customTheme,
        workspaceId: selectedWorkspaceId,
        businessName,
        createdBy: currentUser.uid,
      });
      const docRef = await addDoc(collection(db, 'forms'), formData);

      // Industry templates seed a starter AI knowledge base and follow-up
      // defaults so the AI already knows how to qualify leads for this
      // niche. AI replies stay OFF (settings.autoReply stays false) until
      // the owner reviews the seeded knowledge base in the builder's Quick
      // Setup section and turns replies on themselves.
      if (template?.aiKnowledgeBase) {
        try {
          const kbRef = await addDoc(collection(db, 'knowledge_bases'), {
            workspaceId: selectedWorkspaceId,
            name: template.name,
            content: template.aiKnowledgeBase,
            isShared: false,
            formsUsing: [docRef.id],
            sources: [{ type: 'template', template: template.id, addedAt: new Date() }],
            createdAt: new Date(),
            updatedAt: new Date(),
          });

          await updateDoc(doc(db, 'forms', docRef.id), {
            settings: {
              ...formData.settings,
              knowledgeBaseId: kbRef.id,
              ...(template.followUpDefaults || {}),
            },
          });
        } catch (kbError) {
          console.error('Error seeding template knowledge base:', kbError);
          // Form was already created; the owner can still set up AI replies manually.
        }
      }

      setShowTemplateSelector(false);
      navigate(`/forms/${docRef.id}/edit`);
    } catch (error) {
      console.error('Error creating form:', error);
      showToast('Failed to create form', 'error');
    } finally {
      setIsCreating(false);
    }
  }

  function getFormUrl(formId) {
    return `${window.location.origin}/form/${formId}`;
  }

  function openEmbedSettings(form) {
    setSelectedFormForEmbed(form);
    setShowEmbedSettings(true);
  }

  function openPreview(form) {
    setPreviewForm(form);
    setShowPreview(true);
  }

  async function saveEmbedSettings(settings) {
    if (!selectedFormForEmbed) return;
    
    try {
      await updateDoc(doc(db, 'forms', selectedFormForEmbed.id), {
        embedSettings: settings,
        updatedAt: new Date(),
      });
      showToast('Embed settings saved!');
      setShowEmbedSettings(false);
      // Forms list refreshes automatically via the onSnapshot realtime listener
    } catch (error) {
      console.error('Error saving embed settings:', error);
      showToast('Failed to save embed settings', 'error');
    }
  }

  const pagePad = isMobile ? '1rem 1rem 2rem' : '24px 32px 48px';
  const tierMeta = usagePillText({
    tier: usage?.tier || userProfile?.tier,
    usage,
    usageLoading,
    userProfile,
  }).replace(' · ', ' | ');

  return (
    <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
      <div className="today-page forms-list-page" style={{ padding: pagePad, maxWidth: 1200, margin: '0 auto' }}>
        <div className="today-header">
          <div className="today-header-left">
            <h1 className="today-h1">Forms</h1>
            <span className="forms-list-tier-meta mono">{tierMeta}</span>
          </div>
          <button type="button" className="inbox-pill-btn-primary" onClick={handleCreateForm}>
            + Create New Form
          </button>
        </div>

        {showTemplateSelector && (
          <TemplateSelector
            onSelect={createNewForm}
            onCancel={() => setShowTemplateSelector(false)}
            creating={isCreating}
          />
        )}

        {loading ? (
          <FormsListSkeleton />
        ) : forms.length === 0 ? (
          <div className="today-access-card">
            <p style={{ fontSize: '1.1rem', marginBottom: '1rem', color: 'var(--ink)' }}>No forms yet</p>
            <button type="button" className="inbox-pill-btn-primary" onClick={handleCreateForm}>
              Create Your First Form
            </button>
          </div>
        ) : (
          <div className="forms-list-grid">
            {forms.map((form) => (
              <div key={form.id} className="today-panel forms-list-card">
                <div className="forms-list-card-body">
                  <div className="forms-list-card-name">{form.name}</div>
                  {form.settings?.autoReply && !form.settings?.knowledgeBaseId && (
                    <div className="fb-callout warn" style={{ marginTop: '0.75rem', marginBottom: '0.75rem' }}>
                      AI reply is on but no knowledge base is linked, replies won&apos;t send.{' '}
                      <button
                        type="button"
                        className="today-link-accent"
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
                        onClick={() => navigate(`/forms/${form.id}/edit?tab=ai-reply`)}
                      >
                        Link a KB
                      </button>
                    </div>
                  )}
                  {form.createdAt && (
                    <div className="forms-list-card-meta">
                      Created {(form.createdAt.toDate ? form.createdAt.toDate() : new Date(form.createdAt)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  )}
                  {/* TODO: add last-submission date + domain once cheaply available */}
                  <div className="forms-list-card-meta">{form.fields?.length || 0} fields</div>
                  <div className="forms-list-actions">
                    <button type="button" className="inbox-pill-btn" onClick={() => openPreview(form)}>
                      View Form
                    </button>
                    <button type="button" className="inbox-pill-btn-primary" onClick={() => navigate(`/forms/${form.id}/edit`)}>
                      Edit
                    </button>
                    <button type="button" className="inbox-pill-btn" onClick={() => navigate(`/leads/${form.id}`)}>
                      Manage Leads
                    </button>
                    <button type="button" className="inbox-pill-btn" onClick={() => openEmbedSettings(form)}>
                      Embed Code & Settings
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Embed Settings Modal */}
      {showEmbedSettings && selectedFormForEmbed && (
        <EmbedSettings
          formId={selectedFormForEmbed.id}
          embedSettings={selectedFormForEmbed.embedSettings}
          onSave={saveEmbedSettings}
          onClose={() => {
            setShowEmbedSettings(false);
            setSelectedFormForEmbed(null);
          }}
        />
      )}

      {/* Form Preview Modal */}
      {showPreview && previewForm && (
        <div className="fb-modal-overlay">
          <div className={`fb-modal ${previewMode === 'mobile' ? 'preview-mobile' : 'preview-desktop'}`}>
            <div className="fb-modal-header">
              <h2 className="fb-modal-title">Form Preview - {previewForm.name}</h2>
              <div className="fb-modal-header-actions">
                <div className="fb-segmented">
                  <button
                    type="button"
                    className={`fb-segmented-btn${previewMode === 'desktop' ? ' active' : ''}`}
                    onClick={() => setPreviewMode('desktop')}
                  >
                    Desktop
                  </button>
                  <button
                    type="button"
                    className={`fb-segmented-btn${previewMode === 'mobile' ? ' active' : ''}`}
                    onClick={() => setPreviewMode('mobile')}
                  >
                    Mobile
                  </button>
                </div>
                <button
                  type="button"
                  className="inbox-pill-btn fb-modal-close"
                  onClick={() => {
                    setShowPreview(false);
                    setPreviewForm(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            <div
              className="fb-preview-frame"
              style={{
                overflow: 'hidden',
                padding: 0,
                height: previewMode === 'mobile' ? '600px' : '700px',
              }}
            >
              <iframe
                src={getFormUrl(previewForm.id)}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                title={`Preview of ${previewForm.name}`}
              />
            </div>

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <a
                href={getFormUrl(previewForm.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="today-link-accent"
              >
                Open in new tab →
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
